// AIQuestionsTester.js - Helper for testing AI questions with dedicated API
import axios from 'axios';

/**
 * Service to handle AI questions testing using a dedicated API endpoint
 */
class AIQuestionsTester {
    /**
     * Test AI questions against a call record
     *
     * @param {Object} formattedQuestions - Questions object with proper structure and type information
     * @param {Object} callData - The call data to test with (either an ID or complete call object)
     * @param {Object} config - Optional AI configuration
     * @param {String} tenant - The tenant to use for the API call
     * @param {String} authToken - Auth0 JWT token for authorization
     * @returns {Promise<Object>} - Object with test results for each question
     */
    static async testQuestions(questions, callData, config = {}, tenant = null, authToken = null) {
        try {
            // Format questions for the API in the expected structure
            const formattedQuestions = this.formatQuestionsForAPI(questions);

            // Add AI configuration if provided
            if (config.model || config.temperature || config.systemPrompt) {
                formattedQuestions._AIConfiguration = {
                    model: config.model,
                    temperature: config.temperature,
                    systemPrompt: config.systemPrompt
                };
            }

            // Structure payload for the dedicated API
            const payload = {
                questions: formattedQuestions,
                call: callData
            };

            // Get auth token if not provided
            if (!authToken) {
                // Try to get token from local storage (where Auth0 typically stores it)
                const tokenData = localStorage.getItem('@@auth0spajs@@::dyD6ZcKg5xeZqW9Vw1QkEb4xHd1RvFuh::default::openid profile email offline_access');
                if (tokenData) {
                    try {
                        const parsedData = JSON.parse(tokenData);
                        if (parsedData.body && parsedData.body.access_token) {
                            authToken = parsedData.body.access_token;
                        }
                    } catch (e) {
                        console.error('Error parsing auth token:', e);
                    }
                }
            }

            if (!authToken) {
                throw new Error('Authentication token is required for API calls');
            }

            // Make API call to the dedicated endpoint
            const response = await axios.post('/api/evaluate-questions', payload, {
                headers: {
                    'Content-Type': 'application/json',
                    // Include auth token for authentication
                    'Authorization': `Bearer ${authToken}`,
                    // Include tenant information if available
                    ...(tenant && { 'x-tenant': tenant }),
                }
            });

            // Process the answers from the API response
            return this.processAPIResponse(response.data, questions);
        } catch (error) {
            console.error('Error testing AI questions:', error);
            throw new Error(`Failed to test questions: ${error.message}`);
        }
    }

    /**
     * Formats the form-based questions into the structure expected by the answerConversationQuestions function
     *
     * @param {Array} questions - Array of question objects from the form
     * @returns {Object} - Object formatted for the API
     */
    static formatQuestionsForAPI(questions) {
        const formattedQuestions = {};

        // Ensure questions is always treated as an array
        const questionsArray = Array.isArray(questions) ? questions : [];

        questionsArray.forEach(question => {
            // Skip empty questions without field name or question text
            if (!question || (!question.fieldName && !question.question)) return;

            console.log("Prepping Question " + JSON.stringify(question,null,2));

            // Use field name or generate a fallback name if missing
            const fieldName = question.fieldName || `Question_${Object.keys(formattedQuestions).length + 1}`;

            // Prepare the question text, potentially with pattern requirements
            let questionText = typeof question.question === 'string' ? question.question :
                (question.question || `Question ${fieldName}`);

            // If pattern is specified, append it to the question text
            if (question.pattern) {
                questionText += ` Response must match the regular expression: ${question.pattern}`;
            }

            // Create the question object with the question text
            formattedQuestions[fieldName] = {
                question: questionText,
                type: question.type || 'string'
            };

            // Handle allowed values - this is a critical fix
            // In the AIQuestionEditor settings panel, when a user selects "From list" in the
            // "Allowed Values" dropdown, the values are stored in question.allowedValues
            if (question.allowedValues && Array.isArray(question.allowedValues) && question.allowedValues.length > 0) {
                formattedQuestions[fieldName].enum = [...question.allowedValues];

                // Also append these values to the question text for clarity
                const valuesString = question.allowedValues.join(', ');
                questionText += ` Choose from these values: ${valuesString}.`;
                formattedQuestions[fieldName].question = questionText;
            }

            // Add constraint fields if they exist in the original question
            const constraintFields = [
                'required',
                'minimum',
                'maximum',
                'minLength',
                'maxLength',
                'format'
            ];

            constraintFields.forEach(field => {
                if (question[field] !== undefined && question[field] !== null) {
                    formattedQuestions[fieldName][field] = question[field];
                }
            });

            // Handle allowMultiple flag - convert to array property
            if (question.allowMultiple === true) {
                formattedQuestions[fieldName].array = true;
            }

            // Debug logging to help trace the issue
            console.log(`Formatted question for ${fieldName}:`, formattedQuestions[fieldName]);
        });

        // Log the entire formatted questions object
        console.log("Formatted Questions for API:", formattedQuestions);

        return formattedQuestions;
    }

    /**
     * Process the API response and map answers back to questions
     *
     * @param {Object} response - The API response
     * @param {Array} originalQuestions - The original questions from the form
     * @returns {Object} - Mapped results for each question by index
     */
    static processAPIResponse(response, originalQuestions) {
        // The response should contain the answers directly
        const answers = response.answers || {};
        const results = {};

        // Map answers back to original questions by field name
        originalQuestions.forEach((question, index) => {
            if (!question.fieldName) return;

            // Get answer for this field
            const answer = answers[question.fieldName];

            // Store the result by index for easier consumption in the component
            results[index] = answer;
        });

        return results;
    }

    /**
     * Generate a mock result for testing without API
     * (useful for development or when API is unavailable)
     *
     * @param {Array} questions - Array of question objects
     * @returns {Object} - Object with mock test results
     */
    static async mockTestQuestions(questions) {
        const mockResults = {};

        const generateMockAnswer = (question) => {
            const type = question.type || 'string';

            // Generate different types of mock answers based on question type
            switch (type) {
                case 'number':
                case 'integer':
                    return Math.floor(Math.random() * 100);
                case 'boolean':
                    return Math.random() > 0.5;
                case 'string':
                    if (question.allowedValues && question.allowedValues.length > 0) {
                        // Return a random value from allowed values
                        return question.allowedValues[Math.floor(Math.random() * question.allowedValues.length)];
                    }
                    return `Mock answer for: ${question.question.substring(0, 20)}...`;
                default:
                    return `Mock answer (${type})`;
            }
        };

        // Create mock results for each question
        questions.forEach((question, index) => {
            if (!question.fieldName || !question.question) return;
            mockResults[index] = generateMockAnswer(question);
        });

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));

        return mockResults;
    }
}

export default AIQuestionsTester;
